import {
  BUSINESS_DAYS_PER_MONTH,
  HOURS_PER_DAY,
  MINUTES_PER_HOUR,
  MONTHS_PER_YEAR
} from "../../lib/constants";
import { formatCurrency, formatDays, formatNumber } from "../../lib/currency";
import type { CurrencyCode } from "../../lib/types";
import type { CustomerPortalInputs } from "./defaults";
import type { CustomerPortalResult } from "./formula";

export function customerPortalHelpHtml(
  inputs: CustomerPortalInputs,
  result: CustomerPortalResult,
  currency: CurrencyCode
): string {
  const monthlySalaryPerCsr = inputs.annualCsrSalary / MONTHS_PER_YEAR;

  return `
    <article class="help-card">
      <h3>First Year Monthly Value Calculation</h3>
      <div class="help-equation" aria-hidden="true">
        <span class="help-term"><i class="fa-solid fa-users"></i><small>CSR Head Count</small></span>
        <span class="help-op">×</span>
        <span class="help-term"><i class="fa-solid fa-graduation-cap"></i><small>CSR Monthly Salary</small></span>
        <span class="help-op">×</span>
        <span class="help-term"><i class="fa-solid fa-handshake"></i><small>Self-Service %</small></span>
        <span class="help-op">−</span>
        <span class="help-term"><i class="fa-solid fa-dollar-sign"></i><small>Monthly Price</small></span>
        <span class="help-op">−</span>
        <span class="help-term"><i class="fa-solid fa-wrench"></i><small>Implementation Fee / 12</small></span>
      </div>
      <p class="help-formula">CSR head count × CSR monthly salary × self-service % - (monthly price + implementation fee / 12)</p>
      <p class="help-example">
        Example: ${formatNumber(inputs.csrHeadCount)} × ${formatCurrency(monthlySalaryPerCsr, currency)} × ${formatNumber(inputs.selfServiceTransactionPercent)}% - (${formatCurrency(inputs.monthlyPrice, currency)} + ${formatCurrency(inputs.implementationFee, currency)} / ${MONTHS_PER_YEAR}) =
        <strong>${formatCurrency(result.firstYearMonthlyValue, currency)}</strong>
      </p>
      <p class="help-note">Daily value: ${formatCurrency(result.firstYearMonthlyValue / BUSINESS_DAYS_PER_MONTH, currency)} across ${BUSINESS_DAYS_PER_MONTH} business days.</p>
    </article>
    <article class="help-card">
      <h3>Total Monthly CSR Time Saved Calculation</h3>
      <div class="help-equation" aria-hidden="true">
        <span class="help-term"><i class="fa-solid fa-users"></i><small>CSR Head Count</small></span>
        <span class="help-op">×</span>
        <span class="help-term"><i class="fa-solid fa-comments"></i><small>Daily Interactions</small></span>
        <span class="help-op">×</span>
        <span class="help-term"><i class="fa-solid fa-clock"></i><small>Minutes / Interaction</small></span>
        <span class="help-op">×</span>
        <span class="help-term"><i class="fa-solid fa-handshake"></i><small>Self-Service %</small></span>
        <span class="help-op">×</span>
        <span class="help-term"><i class="fa-solid fa-calendar-days"></i><small>${BUSINESS_DAYS_PER_MONTH} Business Days</small></span>
      </div>
      <p class="help-formula">CSR head count × daily interactions × minutes per interaction × self-service % × ${BUSINESS_DAYS_PER_MONTH} business days</p>
      <p class="help-example">
        Example: ${formatNumber(inputs.csrHeadCount)} × ${formatNumber(inputs.dailyInteractionsPerCsr)} × ${formatNumber(inputs.minutesPerInteraction)} × ${formatNumber(inputs.selfServiceTransactionPercent)}% × ${BUSINESS_DAYS_PER_MONTH} =
        <strong>${formatNumber(result.totalMinutesSavedPerMonth)} minutes</strong> or
        <strong>${formatDays(result.totalDaysSavedPerMonth)} days</strong>
      </p>
      <p class="help-note">Days = minutes ÷ ${MINUTES_PER_HOUR} ÷ ${HOURS_PER_DAY}.</p>
    </article>
  `;
}
